import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { ArrowRight, CheckCircle2, FileText, Loader2, ShoppingBag } from "lucide-react";

import { PageHero, Section } from "@/components/layout/Page";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import type { Order } from "@/lib/types";
import { getCurrentLocale } from "@/lib/utils/locale";
import { COMPANY } from "@/lib/company";

/**
 * Where a completed store order lands.
 *
 * Checkout used to end in a message rendered in place, which meant there was
 * no address for "your order went through" — nothing to link to from an email,
 * nothing to reload, nothing to send a colleague.
 *
 * The order id comes from the URL but nothing about the order is *read* from
 * it: the total and status are fetched from /api/portal/orders, which is
 * session-scoped, so editing the number in the address bar shows you nothing
 * that is not already yours.
 */
const searchSchema = z.object({
  order: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute("/shop/order-confirmed")({
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({
    meta: [
      {
        title:
          getCurrentLocale() === "tr"
            ? `Sipariş Onaylandı | ${COMPANY.compactName}`
            : `Order confirmed | ${COMPANY.tradeName}`,
      },
      // A receipt is not a landing page.
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OrderConfirmed,
});

function OrderConfirmed() {
  const { t } = useI18n();
  const { order: orderId } = Route.useSearch();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(Boolean(orderId));

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    fetch("/api/portal/orders")
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((payload: { data?: Order[] }) => {
        if (cancelled) return;
        setOrder(payload.data?.find((o) => o.orderId === orderId) ?? null);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <>
      <PageHero
        eyebrow={t("store.confirmed.eyebrow")}
        title={t("store.confirmed.title")}
        description={t("store.confirmed.description")}
      />

      <Section>
        <div className="mx-auto max-w-xl space-y-6">
          <div className="card-flat space-y-4 rounded-2xl p-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />

            {loading ? (
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
            ) : order ? (
              <dl className="space-y-2 text-sm">
                <div className="flex items-baseline justify-between gap-4 border-b border-border/60 pb-2">
                  <dt className="text-muted-foreground">{t("store.confirmed.reference")}</dt>
                  <dd className="font-display font-bold text-navy">{order.id}</dd>
                </div>
                {order.total && (
                  <div className="flex items-baseline justify-between gap-4 border-b border-border/60 pb-2">
                    <dt className="text-muted-foreground">{t("store.confirmed.total")}</dt>
                    <dd className="font-display font-bold text-navy">{order.total}</dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-muted-foreground">{t("store.confirmed.status")}</dt>
                  <dd className="font-semibold text-navy">{order.status}</dd>
                </div>
              </dl>
            ) : (
              // Never claim an order we could not read. The order may still be
              // perfectly fine — the session may simply have expired.
              <p className="text-sm text-muted-foreground">{t("store.confirmed.notLoaded")}</p>
            )}

            <p className="text-xs leading-relaxed text-muted-foreground">
              {t("store.confirmed.nextSteps")}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-xl font-bold">
              <Link to="/account/orders">
                <FileText className="me-2 h-4 w-4" /> {t("store.confirmed.viewOrders")}
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-xl font-bold">
              <Link to="/shop">
                <ShoppingBag className="me-2 h-4 w-4" /> {t("store.confirmed.keepShopping")}
                <ArrowRight className="ms-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
